import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { ActivityTimeline } from './ActivityTimeline';
import { AudienceTag } from './AudienceTag';
import { CommentComposer } from './CommentComposer';
import { StatusBadge } from './StatusBadge';
import type { ActivityEvent } from '../../types/activity';
import type { Condition } from '../../types/conditions';
interface CommentsDrawerProps {
  condition: Condition | null;
  events: ActivityEvent[];
  onClose: () => void;
  onSubmit: (conditionId: string, body: string, mentions: string[], sentToBorrower: boolean) => void;
  borrowerSendConfirmed: boolean;
  onBorrowerSendConfirmed: () => void;
}

/** Per-condition activity: comments and system events in one timeline, plus the composer. */
export function CommentsDrawer({
  condition,
  events,
  onClose,
  onSubmit,
  borrowerSendConfirmed,
  onBorrowerSendConfirmed
}: CommentsDrawerProps) {
  useEffect(() => {
    if (!condition) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [condition, onClose]);
  return <AnimatePresence data-id="element-108" data-mp="1uyv5y8pahtyl">
      {condition && <>
          <motion.div className="fixed inset-0 z-40 bg-slate-900/20" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} aria-hidden="true" data-id="element-109" data-mp="1zxohhvl0naw4" />
          <motion.aside role="dialog" aria-modal="true" aria-labelledby="activity-title" className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col bg-white shadow-2xl" initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        stiffness: 400,
        damping: 40
      }} data-id="element-110" data-mp="1qzmamzelpo0x">
            <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4" data-id="element-111" data-mp="39wh9kewvpqc1">
              <div className="min-w-0" data-id="element-112" data-mp="3560qae6dnahr">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500" data-id="element-113" data-mp="35466x4ar7qfs">Activity</p>
                <h2 id="activity-title" className="mt-0.5 text-base font-semibold text-slate-900" data-id="element-114" data-mp="3077d6mv05li4">
                  {condition.name}
                </h2>
                <div className="mt-1.5 flex flex-wrap items-center gap-2" data-id="element-115" data-mp="3sv3btx44tnhj">
                  <StatusBadge status={condition.status} data-id="element-116" data-mp="3ssz5z9moaeem" />
                  <AudienceTag borrower={condition.borrowerVisible} data-id="element-117" data-mp="3nxjugoc50uld" />
                </div>
                <p className="mt-1.5 text-xs text-slate-500" data-id="element-118" data-mp="3iw46aneny7yr">{condition.description}</p>
              </div>
              <button type="button" onClick={onClose} aria-label="Close activity" className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-119" data-mp="3eush2izpbor3">
                <XIcon className="h-5 w-5" data-id="element-120" data-mp="izxjt9pd4jll" />
              </button>
            </header>

            <ActivityTimeline key={condition.id} comments={condition.comments} events={events} data-id="element-121" data-mp="n0r7c1yw7wdy" />

            <CommentComposer key={`composer-${condition.id}`} onSubmit={(body, mentions, toBorrower) => onSubmit(condition.id, body, mentions, toBorrower)} borrowerSendConfirmed={borrowerSendConfirmed} onBorrowerSendConfirmed={onBorrowerSendConfirmed} data-id="element-122" data-mp="ry9blf1ytnfm" />
          </motion.aside>
        </>}
    </AnimatePresence>;
}